import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../lib/utils';
import { ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';

// Enhanced prose styles for better markdown rendering
const proseStyles = cn(
  'prose prose-slate max-w-none',
  // Headings - bold, proper sizing, spacing
  'prose-h1:text-2xl prose-h1:font-bold prose-h1:text-[#1E1B4B] prose-h1:mt-6 prose-h1:mb-4 prose-h1:leading-tight',
  'prose-h2:text-xl prose-h2:font-bold prose-h2:text-[#1E1B4B] prose-h2:mt-5 prose-h2:mb-3 prose-h2:leading-tight',
  'prose-h3:text-lg prose-h3:font-semibold prose-h3:text-[#1E1B4B] prose-h3:mt-4 prose-h3:mb-2',
  'prose-h4:text-base prose-h4:font-semibold prose-h4:text-slate-800 prose-h4:mt-3 prose-h4:mb-2',
  'prose-h5:text-sm prose-h5:font-semibold prose-h5:text-slate-700 prose-h5:mt-2 prose-h5:mb-1',
  'prose-h6:text-sm prose-h6:font-medium prose-h6:text-slate-600 prose-h6:mt-2 prose-h6:mb-1',
  // Paragraphs
  'prose-p:text-slate-600 prose-p:leading-relaxed prose-p:my-3',
  // Strong/Bold
  'prose-strong:text-slate-800 prose-strong:font-semibold',
  // Lists
  'prose-ul:text-slate-600 prose-ul:my-3 prose-ul:pl-5',
  'prose-ol:text-slate-600 prose-ol:my-3 prose-ol:pl-5',
  'prose-li:text-slate-600 prose-li:my-1',
  'prose-li:marker:text-cyan-500',
  // Links
  'prose-a:text-cyan-600 prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-cyan-700',
  // Code
  'prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:text-slate-700 prose-code:before:content-none prose-code:after:content-none',
  'prose-pre:bg-slate-800 prose-pre:text-slate-100 prose-pre:rounded-lg prose-pre:p-4 prose-pre:my-4 prose-pre:overflow-x-auto',
  // Blockquotes
  'prose-blockquote:border-l-4 prose-blockquote:border-cyan-500 prose-blockquote:bg-cyan-50 prose-blockquote:pl-4 prose-blockquote:py-2 prose-blockquote:my-4 prose-blockquote:italic prose-blockquote:text-slate-600',
  // Horizontal rules
  'prose-hr:border-slate-200 prose-hr:my-6'
);

// Custom table component styles
const tableStyles = {
  table: 'w-full border-collapse my-4 text-sm',
  thead: 'bg-slate-100',
  th: 'border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700',
  td: 'border border-slate-200 px-3 py-2 text-slate-600',
  tr: 'even:bg-slate-50'
};

// Custom components for ReactMarkdown
const markdownComponents = {
  table: ({ children }) => (
    <div className="overflow-x-auto my-4">
      <table className={tableStyles.table}>{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className={tableStyles.thead}>{children}</thead>,
  th: ({ children }) => <th className={tableStyles.th}>{children}</th>,
  td: ({ children }) => <td className={tableStyles.td}>{children}</td>,
  tr: ({ children, ...props }) => (
    <tr className={props.isHeader ? '' : tableStyles.tr}>{children}</tr>
  ),
  // Better heading spacing
  h1: ({ children }) => <h1 className="text-2xl font-bold text-[#1E1B4B] mt-6 mb-4 leading-tight">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-bold text-[#1E1B4B] mt-5 mb-3 leading-tight">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-semibold text-[#1E1B4B] mt-4 mb-2">{children}</h3>,
  h4: ({ children }) => <h4 className="text-base font-semibold text-slate-800 mt-3 mb-2">{children}</h4>,
  h5: ({ children }) => <h5 className="text-sm font-semibold text-slate-700 mt-2 mb-1">{children}</h5>,
  h6: ({ children }) => <h6 className="text-sm font-medium text-slate-600 mt-2 mb-1">{children}</h6>,
  // Better paragraph spacing
  p: ({ children }) => <p className="text-slate-600 leading-relaxed my-3">{children}</p>,
  // Better list styling
  ul: ({ children }) => <ul className="list-disc pl-5 my-3 space-y-1 text-slate-600">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 my-3 space-y-1 text-slate-600">{children}</ol>,
  li: ({ children }) => <li className="text-slate-600">{children}</li>,
  // Strong text
  strong: ({ children }) => <strong className="font-semibold text-slate-800">{children}</strong>,
  // Code blocks
  code: ({ inline, children }) => 
    inline ? (
      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm text-slate-700">{children}</code>
    ) : (
      <code>{children}</code>
    ),
  // Blockquotes
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-cyan-500 bg-cyan-50 pl-4 py-2 my-4 italic text-slate-600">
      {children}
    </blockquote>
  ),
};

export function MarkdownContent({ 
  content, 
  title = 'Content',
  maxHeight = 150, 
  className,
  showSeeAll = true 
}) {
  const [showFullContent, setShowFullContent] = useState(false);
  
  if (!content) return <p className="text-slate-400 text-sm italic">Not available</p>;

  const isLongContent = content.length > 500;

  return (
    <>
      <div className={cn('relative', className)}>
        <div 
          className={cn(
            proseStyles,
            'bg-slate-50 p-4 rounded-lg overflow-hidden'
          )}
          style={{ maxHeight: isLongContent && showSeeAll ? maxHeight : 'none' }}
        >
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {content}
          </ReactMarkdown>
        </div>
        
        {/* Gradient overlay for long content */}
        {isLongContent && showSeeAll && (
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none rounded-b-lg" />
        )}
        
        {/* See All button */}
        {isLongContent && showSeeAll && (
          <button
            onClick={() => setShowFullContent(true)}
            className={cn(
              'absolute bottom-2 left-1/2 -translate-x-1/2',
              'px-4 py-1.5 bg-white border border-slate-200 rounded-full',
              'text-sm font-medium text-cyan-600 hover:bg-cyan-50',
              'shadow-sm hover:shadow-md transition-all',
              'flex items-center gap-1'
            )}
            data-testid="see-all-btn"
          >
            Read All
            <ChevronDown className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Full Content Modal */}
      <Dialog open={showFullContent} onOpenChange={setShowFullContent}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1E1B4B] font-['Manrope']">
              {title}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className={proseStyles}>
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {content}
              </ReactMarkdown>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function TextContent({ 
  content, 
  title = 'Content',
  maxHeight = 150, 
  className,
  showSeeAll = true 
}) {
  const [showFullContent, setShowFullContent] = useState(false);
  
  if (!content) return <p className="text-slate-400 text-sm italic">Not available</p>;

  const isLongContent = content.length > 400;

  return (
    <>
      <div className={cn('relative', className)}>
        <pre 
          className={cn(
            'whitespace-pre-wrap text-sm text-slate-700',
            'bg-slate-50 p-4 rounded-lg overflow-hidden font-sans'
          )}
          style={{ maxHeight: isLongContent && showSeeAll ? maxHeight : 'none' }}
        >
          {content}
        </pre>
        
        {/* Gradient overlay for long content */}
        {isLongContent && showSeeAll && (
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none rounded-b-lg" />
        )}
        
        {/* See All button */}
        {isLongContent && showSeeAll && (
          <button
            onClick={() => setShowFullContent(true)}
            className={cn(
              'absolute bottom-2 left-1/2 -translate-x-1/2',
              'px-4 py-1.5 bg-white border border-slate-200 rounded-full',
              'text-sm font-medium text-cyan-600 hover:bg-cyan-50',
              'shadow-sm hover:shadow-md transition-all',
              'flex items-center gap-1'
            )}
            data-testid="see-all-btn"
          >
            Read All
            <ChevronDown className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Full Content Modal */}
      <Dialog open={showFullContent} onOpenChange={setShowFullContent}>
        <DialogContent className="max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1E1B4B] font-['Manrope']">
              {title}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
              {content}
            </pre>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
